import { Box, Group, Text } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * MED-1093 / MED-1092 — shared visual primitives + tokens for the authored
 * agenda sections, used by the coach `AuthoredSessionAgendaAccordion` and the
 * peer-leader `PeerLeaderAgendaSectionList` so both render the same authored
 * content identically (one source of truth — no drift).
 */

// Neutral "Step Content" box.
export const STEP_CONTENT_BG = '#F9FAFB';
export const STEP_CONTENT_BORDER = '#E5E7EB';
export const STEP_LABEL_COLOR = '#9CA3AF';
// The accordion is a single outlined container; rows are divided by bottom
// borders, expanded rows fill grey. Numbered circle: navy when expanded, light
// blue when collapsed.
export const ACCORDION_BORDER = '#E5E7EB';
export const ROW_EXPANDED_BG = '#FAFAFA';
export const STEP_NUM_ACTIVE_BG = '#203358';
export const STEP_NUM_INACTIVE_BG = '#EBF2FA';
// "What participants see" keeps a distinct green-tinted background + green accent
// to differentiate it from the neutral "Step Content" box; everything else
// (bare icon, label styling, border, radius) is shared via SectionContentBox.
export const PARTICIPANT_BG = 'rgba(74, 124, 89, 0.05)';
export const PARTICIPANT_ACCENT = 'var(--mantine-color-medleyGreen-6)';

/**
 * Bordered, lightly-tinted content box with a bare-icon uppercase label — used
 * for both "Step Content" (neutral) and "What participants see" (green). Same
 * header styling, border, and radius; only background, icon, accent, label, and
 * content differ.
 */
export function SectionContentBox({
  icon,
  label,
  labelColor,
  background,
  children,
}: {
  icon: ReactNode;
  label: string;
  labelColor: string;
  background: string;
  children: ReactNode;
}) {
  return (
    <Box
      style={{
        border: `1px solid ${STEP_CONTENT_BORDER}`,
        borderRadius: 8,
        background,
        padding: '12px 14px',
      }}
    >
      <Group gap={6} align="center" mb={8}>
        {icon}
        <Text
          size="xs"
          tt="uppercase"
          fw={600}
          fz={10}
          c={labelColor}
          style={{ letterSpacing: '0.5px' }}
        >
          {label}
        </Text>
      </Group>
      {children}
    </Box>
  );
}

/** Numbered section circle: navy filled when active (expanded), light-blue when collapsed. */
export function SectionNumber({ n, active }: { n: number; active: boolean }) {
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: active ? STEP_NUM_ACTIVE_BG : STEP_NUM_INACTIVE_BG,
        color: active ? 'white' : STEP_NUM_ACTIVE_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: 12,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {/* The digit's font metrics sit ~1px high in the circle; nudge down. */}
      <span style={{ display: 'block', transform: 'translateY(1px)' }}>
        {n}
      </span>
    </div>
  );
}
